import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Download, FileType2, Pencil, Trash2, FolderInput, CheckSquare, Square } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import EditAssetModal from "@/components/EditAssetModal";
import { confirmDialog } from "@/components/ConfirmDialog";
import { pickFolder } from "@/components/MoveToFolderModal";
import { downloadAssetFile } from "@/lib/downloadAsset";

export default function AssetCard({ asset, aspect = "square", onChanged, selectMode = false, selected = false, onToggleSelect }) {
  const { user } = useAuth();
  const location = useLocation();
  const [editing, setEditing] = useState(false);
  const canManage = user && (user.role === "admin");
  // Viewers are locked out of downloading artwork; anyone can download everything else.
  const canDownload = !(user && user.role === "viewer" && (asset.category || "").toLowerCase() === "artwork");

  async function handleDownload(e) {
    e.preventDefault();
    e.stopPropagation();
    await downloadAssetFile(asset, asset.file_type);
  }

  async function handleDelete(e) {
    e.preventDefault();
    e.stopPropagation();
    const ok = await confirmDialog({
      title: `Delete "${asset.title}"?`,
      description: "This cannot be undone.",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/assets/${asset.id}`);
      toast.success("Asset deleted");
      onChanged && onChanged({ deletedId: asset.id });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function handleEdit(e) {
    e.preventDefault();
    e.stopPropagation();
    setEditing(true);
  }

  const aspectCls = aspect === "portrait" ? "aspect-[3/4]" : aspect === "wide" ? "aspect-[16/9]" : "aspect-square";

  // In selection mode, clicking the card toggles selection instead of navigating.
  const cardInner = (
    <>
      <div className={`${aspectCls} bg-muted relative overflow-hidden`}>
        {asset.preview_url ? (
          <img src={asset.preview_url} alt={asset.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-700" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground"><FileType2 className="w-8 h-8" /></div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.2em] bg-black/70 text-white rounded-sm">{asset.file_type}</span>
          {asset.states?.length > 0 && (
            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.2em] bg-white/80 text-black rounded-sm">{asset.states.join("·")}</span>
          )}
        </div>
        {selectMode && (
          <div
            className={`absolute inset-0 flex items-center justify-center transition ${
              selected ? "bg-black/40" : "bg-black/0 group-hover:bg-black/10"
            }`}
            data-testid={`asset-select-overlay-${asset.id}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition ${
                selected ? "bg-[#FBC90C] border-[#FBC90C] text-black" : "bg-black/60 border-white/60 text-white"
              }`}
            >
              {selected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
            </div>
          </div>
        )}
        {canManage && !selectMode && (
          <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition">
            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const target = await pickFolder({
                  category: asset.category,
                  currentFolderId: asset.folder_id || null,
                });
                if (target === undefined) return;
                try {
                  await api.post(`/admin/assets/${asset.id}/move`, { folder_id: target });
                  toast.success(target ? "Moved to folder" : "Moved to root");
                  onChanged && onChanged({ deletedId: asset.id });
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              className="p-1.5 rounded-sm bg-white text-black hover:bg-white/90"
              data-testid={`asset-move-${asset.id}`}
              aria-label="Move to folder"
              title="Move to folder"
            >
              <FolderInput className="h-3 w-3" />
            </button>
            <button
              onClick={handleEdit}
              className="p-1.5 rounded-sm bg-white text-black hover:bg-white/90"
              data-testid={`asset-edit-${asset.id}`}
              aria-label="Edit"
              title="Edit"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 rounded-sm text-white hover:opacity-90"
              style={{ background: "#E15C2C" }}
              data-testid={`asset-delete-${asset.id}`}
              aria-label="Delete"
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
        {!selectMode && canDownload && (
          <button
            onClick={handleDownload}
            className="absolute bottom-2 right-2 p-2 rounded-sm bg-white text-black opacity-0 group-hover:opacity-100 transition"
            data-testid={`asset-download-${asset.id}`}
            aria-label="Download"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold truncate">{asset.title}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
          {asset.category}{canManage ? ` · ${asset.downloads || 0} DL` : ""}
        </div>
      </div>
    </>
  );

  return (
    <>
      {selectMode ? (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => { e.preventDefault(); onToggleSelect && onToggleSelect(asset.id); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleSelect && onToggleSelect(asset.id); }
          }}
          className={`group block border rounded-sm overflow-hidden bg-surface hover-lift cursor-pointer ${
            selected ? "border-[#FBC90C] shadow-[0_0_0_2px_#FBC90C]" : "border-border"
          }`}
          data-testid={`asset-card-${asset.id}`}
        >
          {cardInner}
        </div>
      ) : (
        <Link
          to={`/assets/${asset.id}`}
          state={{ from: `${location.pathname}${location.search}` }}
          className="group block border border-border rounded-sm overflow-hidden bg-surface hover-lift"
          data-testid={`asset-card-${asset.id}`}
          draggable={canManage}
          onDragStart={(e) => {
            if (!canManage) return;
            e.dataTransfer.setData("application/x-apollo-asset-id", asset.id);
            e.dataTransfer.effectAllowed = "move";
          }}
        >
          {cardInner}
        </Link>
      )}
      {canManage && (
        <EditAssetModal
          open={editing}
          asset={asset}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { onChanged && onChanged({ updated }); }}
        />
      )}
    </>
  );
}
